import React, { useState, useEffect, useMemo, useRef } from 'react';

import { string, bool } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, Field, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { FaSearch, FaRegImage, FaVideo, FaPlus } from 'react-icons/fa';
import { FiType, FiEdit2 } from 'react-icons/fi';
import { MdOutlineRoundedCorner } from 'react-icons/md';
import { AiOutlineClose, AiOutlineInfoCircle, AiOutlineCheck } from 'react-icons/ai';
import { GoSidebarCollapse, GoSidebarExpand } from 'react-icons/go';
import { BiUpArrow, BiDownArrow } from 'react-icons/bi';

import classNames from 'classnames';
import {
  Form,
  PrimaryButton,
  FieldTextEditor,
  FieldTextInput,
  AddPortfolioImages,
  ValidationError,
  ImageCropModal,
  ImageDropzone,
  SecondaryButton,
  FieldSelectModern,
  IconClose,
  InlineTextButton,
  FieldCheckbox,
  FieldRadioButton,
  Tooltip,
} from '../../components';
import { generateMonthsList, generateYearsList } from '../../util/dates';
import { getEmbedElement, analyzeVideoUrl } from '../../util/videoEmbed';

import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import config from '../../config';

import {
  maxLength,
  composeValidators,
  required,
  textEditorValidation,
  noLinks,
} from '../../util/validators';
import css from './PortfolioWorkForm.css';

const ACCEPT_IMAGES = 'image/*';
const TITLE_MAX_LENGTH = 60;

const Placeholder = () => (
  <div className={css.placeholderContainer}>
    <FaSearch className={css.searchIcon} />
  </div>
);

// Custom title input component that shows only placeholder until focused
const TitleInputWithPlaceholder = ({ id, name, placeholder, maxLength, validate }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  return (
    <Field name={name} validate={validate}>
      {({ input, meta }) => {
        const hasValue = input.value && input.value.trim().length > 0;
        const currentLength = input.value ? input.value.length : 0;

        return (
          <div className={css.title}>
            <div className={css.titleInputWrapper}>
              <textarea
                {...input}
                id={id}
                placeholder={placeholder}
                maxLength={maxLength}
                autoFocus
                onFocus={e => {
                  setIsFocused(true);
                  setHasInteracted(true);
                  input.onFocus(e);
                }}
                onBlur={e => {
                  setIsFocused(false);
                  input.onBlur(e);
                }}
                className={css.titleInput}
                rows={1}
                style={{ resize: 'none', overflow: 'hidden' }}
                onInput={e => {
                  // Auto-resize textarea to fit content
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
              />
              <span className={css.characterCounter}>
                {currentLength}/{maxLength}
              </span>
            </div>
            {meta.touched && meta.error && (
              <div
                style={{
                  color: '#ff0000',
                  fontSize: '14px',
                  marginTop: '4px',
                }}
              >
                {meta.error}
              </div>
            )}
          </div>
        );
      }}
    </Field>
  );
};

// Custom image radius selector with icon-based selection
const ImageRadiusSelector = ({ name, validate }) => {
  return (
    <Field name={name} validate={validate}>
      {({ input, meta }) => {
        const isRounded = input.value === 'true';

        const handleToggle = () => {
          const newValue = isRounded ? 'false' : 'true';
          input.onChange(newValue);
          input.onBlur();
        };

        return (
          <div>
            <button
              type="button"
              onClick={handleToggle}
              className={classNames(
                css.imageRadiusSelectorButton,
                isRounded ? css.selected : css.notSelected
              )}
            >
              <MdOutlineRoundedCorner
                className={classNames(
                  css.imageRadiusSelectorIcon,
                  isRounded ? css.selected : css.notSelected
                )}
              />
            </button>
          </div>
        );
      }}
    </Field>
  );
};

function extractFormElementsFromValues(values) {
  // Collect all keys that match text_N, video_N, or addImage_N
  const dynamicFieldRegex = /^(text|video|addImage)_(\d+)$/;
  const elements = [];

  // 1. Add elements from field keys
  Object.keys(values).forEach(key => {
    const match = key.match(dynamicFieldRegex);
    if (match) {
      let type;
      if (match[1] === 'text') type = 'FieldTextEditor';
      else if (match[1] === 'video') type = 'FieldTextInput';
      else if (match[1] === 'addImage') type = 'AddImages';
      const index = parseInt(match[2], 10);
      elements.push({ type, id: key, index });
    }
  });

  // 2. Add AddImages elements for all indexes in dynamicImageData (if not already present)
  if (values.dynamicImageData && Array.isArray(values.dynamicImageData)) {
    const addImageIndexes = new Set(values.dynamicImageData.map(d => d.elementIndex));
    addImageIndexes.forEach(index => {
      if (!elements.find(e => e.type === 'AddImages' && e.index === index)) {
        elements.push({ type: 'AddImages', id: `addImage_${index}`, index });
      }
    });
  }

  // 3. Sort by index to preserve order
  elements.sort((a, b) => a.index - b.index);
  return elements;
}

// Helper to remap element indexes to sequential order for saving
function remapElementIndexes(formElements, values, dynamicImages) {
  // Map: oldIndex -> newIndex (sequential)
  const indexMap = {};
  let nextIndex = 0;
  formElements.forEach(el => {
    indexMap[el.index] = nextIndex++;
  });

  // Remap all dynamic fields in values
  const remappedValues = { ...values };

  // Remap dynamic fields (text_N, video_N, addImage_N)
  Object.keys(values).forEach(key => {
    const match = key.match(/^(text|video|addImage)_(\d+)$/);
    if (match) {
      const oldIndex = parseInt(match[2], 10);
      const newIndex = indexMap[oldIndex];
      if (newIndex !== undefined && newIndex !== oldIndex) {
        remappedValues[`${match[1]}_${newIndex}`] = values[key];
        delete remappedValues[key];
      }
    }
  });

  // Remap dynamicImageData
  if (Array.isArray(values.dynamicImageData)) {
    remappedValues.dynamicImageData = values.dynamicImageData.map(d => ({
      ...d,
      elementIndex: indexMap[d.elementIndex] ?? d.elementIndex,
    }));
  }

  // Remap dynamicImages (local state)
  const remappedDynamicImages = dynamicImages.map(img => ({
    ...img,
    elementIndex: indexMap[img.elementIndex] ?? img.elementIndex,
  }));

  return { remappedValues, remappedDynamicImages, indexMap };
}

// Helper function to check if HTML content from Quill is effectively empty
function hasActualContent(htmlString) {
  if (!htmlString || htmlString.trim() === '') {
    return false;
  }

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  // Check if there's any actual text content after stripping HTML
  return textContent.trim().length > 0;
}

const PortfolioWorkFormComponent = props => {
  const [imageLoading, setImageLoading] = useState(false);
  const [imageUploadRequested] = useState(false);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [aspect, setAspect] = useState(16 / 9);
  const [formElements, setFormElements] = useState([]);
  const [dynamicImages, setDynamicImages] = useState([]);
  const [currentImageTarget, setCurrentImageTarget] = useState({ type: 'static', index: null });
  const [uploadingElement, setUploadingElement] = useState(null);
  const [submitAction, setSubmitAction] = useState('submit');
  const [savingDraft, setSavingDraft] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [detailsSidebarOpen, setDetailsSidebarOpen] = useState(false);
  const [readyTextEditors, setReadyTextEditors] = useState(new Set());
  const [readyVideos, setReadyVideos] = useState(new Set());
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingImageIds, setDeletingImageIds] = useState(new Set());
  // The image target ({ type, index }) whose cropped image couldn't be encoded
  const [cropErrorTarget, setCropErrorTarget] = useState(null);
  const hiddenFileInputRef = useRef(null);
  const detailsSidebarRef = useRef(null);
  const textEditorRefs = useRef({});

  const {
    rootClassName,
    className,
    inProgress,
    intl,
    images,
    onRemoveImage,
    onUploadImage,
    onManageDisableScrolling,
    skillOptions,
    toolOptions,
    showPublishButton,
    submitButtonText,
    saveButtonText,
    handleCancel,
    isNewPortfolioWorkFlow,
    onDirtyChange,
  } = props;

  // Shown below the cover image or the image element the failed crop was for
  const cropErrorFor = target =>
    cropErrorTarget &&
    cropErrorTarget.type === target.type &&
    cropErrorTarget.index === target.index ? (
      <p className={css.cropError} role="alert">
        <FormattedMessage id="EditListingPhotosForm.imageUploadFailed.uploadFailed" />
      </p>
    ) : null;

  // Close the crop so the error is visible
  const handleCropError = () => {
    setSelectionModalOpen(false);
    setCropErrorTarget(currentImageTarget);
  };

  useEffect(() => {
    // Only update if formElements is empty and values has dynamic fields
    if (
      formElements.length === 0 &&
      props.initialValues &&
      (Object.keys(props.initialValues).some(key => /^(text|video|addImage)_\d+$/.test(key)) ||
        props.initialValues.dynamicImageData?.length > 0)
    ) {
      const elements = extractFormElementsFromValues(props.initialValues);
      if (elements.length > 0) {
        setFormElements(elements);

        // Initialize text editors to ready mode if they have content
        const textEditorFields = elements
          .filter(el => el.type === 'FieldTextEditor')
          .map(el => `text_${el.index}`);

        if (textEditorFields.length > 0) {
          setReadyTextEditors(prev => {
            const newSet = new Set(prev);
            textEditorFields.forEach(fieldName => {
              const content = props.initialValues[fieldName];
              if (content && content.trim() !== '') {
                newSet.add(fieldName);
              }
            });
            return newSet;
          });
        }
      }
    }
    // eslint-disable-next-line
  }, [formElements.length, props.initialValues]);

  // Reset pending delete when user interacts with other elements
  useEffect(() => {
    const handleClickOutside = event => {
      // Check if the click is NOT on a delete button
      const isDeleteButton = event.target.closest(`.${css.deleteButton}`);
      if (!isDeleteButton && pendingDelete !== null) {
        setPendingDelete(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [pendingDelete]);

  // Clean up deletingImageIds after parent removes images
  useEffect(() => {
    if (deletingImageIds.size > 0) {
      const currentImageIds = images.map(img => img.id?.uuid || img.id);
      const idsToRemove = Array.from(deletingImageIds).filter(id => !currentImageIds.includes(id));

      if (idsToRemove.length > 0) {
        setDeletingImageIds(prev => {
          const newSet = new Set(prev);
          idsToRemove.forEach(id => newSet.delete(id));
          return newSet;
        });
      }
    }
  }, [images, deletingImageIds]);

  // ImageCropModal hands over the cropped JPEG, named with the temp image id
  const handleSubmitSelection = resultFile => {
    const tempId = resultFile.name;

    setSelectionModalOpen(false);
    setImageLoading(true);

    if (currentImageTarget.type === 'static') {
      onUploadImage({ id: tempId, file: resultFile });
      setImageLoading(false);
    } else if (currentImageTarget.type === 'dynamic') {
      onUploadImage({
        id: tempId,
        file: resultFile,
      });
      setDynamicImages(prev => [
        ...prev,
        {
          id: tempId,
          file: resultFile,
          elementIndex: currentImageTarget.index,
          aspect,
        },
      ]);

      setImageLoading(false);
    }
    setUploadingElement(null);
    setCropImageSrc(null);
  };

  const classes = classNames(rootClassName || css.root, className);

  const titleMessage = intl.formatMessage({ id: 'EditPortfolioWorkForm.titleLabel' });
  const titlePlaceholderMessage = intl.formatMessage({
    id: 'EditPortfolioWorkForm.titlePlaceholder',
  });
  const titleRequiredMessage = intl.formatMessage({
    id: 'EditPortfolioWorkForm.titleRequired',
  });
  const titleNoLinksMessage = intl.formatMessage({
    id: 'EditPortfolioWorkForm.titleNoLinks',
  });

  const maxLengthMessage = intl.formatMessage(
    { id: 'EditListingDescriptionForm.maxLength' },
    {
      maxLength: TITLE_MAX_LENGTH,
    }
  );
  const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);

  const skillsLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.skillsLabel',
  });
  const toolsLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.toolsLabel',
  });
  const relatedWorkExperienceLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.relatedWorkExperienceLabel',
  });
  const relatedWorkExperienceRequired = intl.formatMessage({
    id: 'EditPortfolioWorkForm.relatedWorkExperienceRequired',
  });
  const relatedWorkExperienceCustomerNameLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.relatedWorkExperienceAgencyLabel',
  });

  const relatedWorkCustomerTypeLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.relatedWorkCustomerTypeLabel',
  });

  const relatedWorkCustomerTypeAgencyLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.relatedWorkCustomerTypeAgencyLabel',
  });
  const relatedWorkCustomerTypeAgencyDescription = intl.formatMessage({
    id: 'EditPortfolioWorkForm.relatedWorkCustomerTypeAgencyDescription',
  });
  const relatedWorkCustomerTypeDirectLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.relatedWorkCustomerTypeDirectLabel',
  });
  const relatedWorkCustomerTypeRequired = intl.formatMessage({
    id: 'EditPortfolioWorkForm.relatedWorkCustomerTypeRequired',
  });

  const roundedCornersLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.imageRadiusSelectLabel',
  });

  const roundedCornersRequired = intl.formatMessage({
    id: 'EditPortfolioWorkForm.imageRadiusSelectRequired',
  });

  const privateWorkLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.privateWorkLabel',
  });

  const privateWorkRequired = intl.formatMessage({
    id: 'EditPortfolioWorkForm.privateWorkRequired',
  });

  const startLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.workedWithStartLabel',
  });
  const endLabel = intl.formatMessage({ id: 'EditPortfolioWorkForm.workedWithEndLabel' });

  const monthPlaceholder = intl.formatMessage({
    id: 'EditPortfolioWorkForm.monthPlaceholder',
  });
  const yearPlaceholder = intl.formatMessage({
    id: 'EditPortfolioWorkForm.yearPlaceholder',
  });
  const yearStartPlaceholder = intl.formatMessage({
    id: 'EditPortfolioWorkForm.yearStartPlaceholder',
  });
  const workContinuesLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.workedWithContinuesLabel',
  });

  const startMonthRequired = intl.formatMessage({
    id: 'EditPortfolioWorkForm.startMonthRequired',
  });
  const startYearRequired = intl.formatMessage({
    id: 'EditPortfolioWorkForm.startYearRequired',
  });
  const endMonthRequired = intl.formatMessage({
    id: 'EditPortfolioWorkForm.endMonthRequired',
  });
  const endYearRequired = intl.formatMessage({
    id: 'EditPortfolioWorkForm.endYearRequired',
  });

  const videoInputLabel = intl.formatMessage({
    id: 'EditPortfolioWorkForm.videoInputLabel',
  });

  const externalLinksMessage = intl.formatMessage({
    id: 'RichTextEditor.externalLinksError',
  });

  const addFirstImageMessage = (
    <FormattedMessage
      id="EditPortfolioWorkForm.addFirstImage"
      values={{
        lineBreak: <br />,
      }}
    />
  );

  const categoryRequired = intl.formatMessage({
    id: 'SignupProviderForms.skillsCategoryRequired',
  });

  const addButtonText = intl.formatMessage({
    id: 'SignupProviderForms.addButton',
  });

  const stepConfig = useMemo(
    () => [
      {
        id: 1,
        title: intl.formatMessage({
          id: 'EditPortfolioWorkForm.stepCreateTitle',
          defaultMessage: 'Create the work',
        }),
      },
      {
        id: 2,
        title: intl.formatMessage({
          id: 'EditPortfolioWorkForm.stepDetailsTitle',
          defaultMessage: 'Add project details',
        }),
      },
    ],
    [intl]
  );

  // Handler to add a new element with a unique index without re-indexing others
  const addFormElement = (type, position = null) => {
    setFormElements(prev => {
      // Find the next unique index
      const nextIndex = prev.length > 0 ? Math.max(...prev.map(e => e.index)) + 1 : 0;
      const newElement = { type, id: Date.now(), index: nextIndex };

      // If AddImages, open cropping modal immediately
      if (type === 'AddImages') {
        setCurrentImageTarget({ type: 'dynamic', index: nextIndex });
        setTimeout(() => {
          if (hiddenFileInputRef.current) {
            hiddenFileInputRef.current.value = '';
            hiddenFileInputRef.current.click();
          }
        }, 0);
      }

      // If FieldTextEditor, start it in edit mode (not in ready set) and focus it
      if (type === 'FieldTextEditor') {
        const fieldName = `text_${nextIndex}`;
        // Ensure it's not in ready set (starts in edit mode)
        setReadyTextEditors(prev => {
          const newSet = new Set(prev);
          newSet.delete(fieldName);
          return newSet;
        });
        // Focus the editor after it's rendered
        setTimeout(() => {
          const editorRef = textEditorRefs.current[fieldName];
          if (editorRef && editorRef.quillRef) {
            const editor = editorRef.quillRef.getEditor();
            if (editor) {
              editor.focus();
            }
          }
        }, 100);
      }

      if (position === null || position >= prev.length) {
        // Add to end
        return [...prev, newElement];
      }
      // Insert at given position but keep existing indexes intact
      return [...prev.slice(0, position), newElement, ...prev.slice(position)];
    });
  };

  const toggleTextEditorReady = (fieldName, shouldFocus = true) => {
    setReadyTextEditors(prev => {
      const newSet = new Set(prev);
      const wasReady = newSet.has(fieldName);

      if (wasReady) {
        // Switching from ready to edit mode - remove from set and focus
        newSet.delete(fieldName);

        if (shouldFocus) {
          // Focus the editor after state update
          setTimeout(() => {
            const editorRef = textEditorRefs.current[fieldName];
            if (editorRef && editorRef.quillRef) {
              const editor = editorRef.quillRef.getEditor();
              if (editor) {
                editor.focus();
              }
            }
          }, 50);
        }
      } else {
        // Switching from edit to ready mode
        newSet.add(fieldName);
      }
      return newSet;
    });
  };

  const moveElementUp = index => {
    if (index <= 0) return;
    setFormElements(prev => {
      const newElements = [...prev];
      [newElements[index - 1], newElements[index]] = [newElements[index], newElements[index - 1]];
      return newElements;
    });
  };

  const moveElementDown = index => {
    if (index >= formElements.length - 1) return;
    setFormElements(prev => {
      const newElements = [...prev];
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
      return newElements;
    });
  };

  const memoizedInitialValues = useMemo(() => props.initialValues, [props.initialValues]);

  // Map raw form values into the shape the page's submit handler expects.
  // Shared by the normal (validated) submit and the save-as-draft action,
  // which bypasses form validation.
  const buildSubmitValues = values => {
    const { remappedValues, remappedDynamicImages } = remapElementIndexes(
      formElements,
      values,
      dynamicImages
    );

    const dynamicImageData = remappedValues.dynamicImageData || [];

    const tempDynamicImageData = remappedDynamicImages
      .map(img => {
        // Find the corresponding image in images by id
        const found = images.find(i => i.id === img.id);
        if (found) {
          return {
            imageId: found.imageId.uuid,
            elementIndex: img.elementIndex,
            aspect: img.aspect, // <-- Add this line
          };
        }
        return null;
      })
      .filter(Boolean);

    const uploadedDynamicImageData = dynamicImageData.filter(
      d => !tempDynamicImageData.some(t => t.imageId === d.imageId)
    );

    // If uploadedDynamicImageData is missing aspect, add it from dynamicImages
    const uploadedDynamicImageDataWithAspect = uploadedDynamicImageData.map(d => {
      if (d.aspect !== undefined) return d;
      const match = dynamicImages.find(img => img.id === d.imageId || img.id?.uuid === d.imageId);
      return match ? { ...d, aspect: match.aspect } : d;
    });

    const dynamicImageIdObjects = [...uploadedDynamicImageDataWithAspect, ...tempDynamicImageData];

    // Only allow fields with new sequential indexes
    const allowedFields = formElements
      .map((el, i) => {
        if (el.type === 'FieldTextEditor') return `text_${i}`;
        if (el.type === 'FieldTextInput') return `video_${i}`;
        if (el.type === 'AddImages') return `addImage_${i}`;
        return null;
      })
      .filter(Boolean);

    const alwaysAllowedFields = [
      'title',
      'relatedWorkExperienceCustomer',
      'relatedWorkCustomerType',
      'relatedWorkExperienceAgency',
      'startMonth',
      'startYear',
      'endMonth',
      'endYear',
      'experience',
      'tools',
      'privateWork',
      'imageRadius',
      'jobContinues',
    ];
    const fieldsToAllow = new Set([...allowedFields, ...alwaysAllowedFields]);

    const newValues = Object.keys(remappedValues).reduce((acc, key) => {
      if (fieldsToAllow.has(key)) {
        acc[key] = remappedValues[key];
      }
      return acc;
    }, {});

    newValues.dynamicImageIds = dynamicImageIdObjects;
    return newValues;
  };

  // Save as draft without running form validation: drafts may be incomplete,
  // only the title is required (the Marketplace API minimum for a draft).
  const handleSaveDraft = async form => {
    const values = form.getState().values;
    const newValues = { ...buildSubmitValues(values), submitAction: 'saveDraft' };
    if (typeof props.onSubmit === 'function') {
      setSavingDraft(true);
      try {
        await props.onSubmit(newValues, formElements);
      } finally {
        setSavingDraft(false);
      }
    }
  };

  return (
    <FinalForm
      {...props}
      initialValues={memoizedInitialValues}
      keepDirtyOnReinitialize
      mutators={{
        ...arrayMutators,
      }}
      imageUploadRequested={imageUploadRequested}
      imageLoading={imageLoading}
      onSubmit={values => {
        const newValues = { ...buildSubmitValues(values), submitAction };

        if (typeof props.onSubmit === 'function') {
          return props.onSubmit(newValues, formElements);
        }
        return newValues;
      }}
      render={formRenderProps => {
        const {
          form: {
            mutators: { push },
          },
          form,
          handleSubmit,
          submitting,
          values,
          hasValidationErrors,
          invalid,
        } = formRenderProps;

        // Use values from formRenderProps, not props!
        const dynamicImageData = values.dynamicImageData || [];
        const dynamicImageIds =
          dynamicImageData.length > 0
            ? dynamicImageData.map(d => d.imageId)
            : dynamicImages.map(img => img.id);

        // Get all dynamic image ids, including those just uploaded (in dynamicImages state)
        const allDynamicIds = [
          ...dynamicImageIds,
          ...dynamicImages.map(img => img.id?.uuid || img.id),
        ];

        // Only images NOT referenced in any dynamic image ids are static
        // Also exclude images that are currently being deleted (to prevent flickering to static)
        const staticImages = images.filter(img => {
          const imgId = img.id?.uuid || img.id;
          return !allDynamicIds.includes(imgId) && !deletingImageIds.has(imgId);
        });

        // Only images referenced in any dynamic image ids are dynamic
        // Exclude images that are currently being deleted
        const dynImages = images.filter(img => {
          const imgId = img.id?.uuid || img.id;
          return allDynamicIds.includes(imgId) && !deletingImageIds.has(imgId);
        });

        const hideEndDate = values.jobContinues;

        const imageUploadInProgress = value => {
          setImageLoading(value);
        };

        const handleDelete = index => {
          // Get the element to identify it uniquely
          const elementToDelete = formElements[index];
          if (!elementToDelete) return;

          const elementId = elementToDelete.id;

          // Two-step confirmation: first click marks for deletion, second click confirms
          if (pendingDelete === elementId) {
            // Second click - actually delete

            // Don't allow deletion if this element is currently uploading
            if (uploadingElement === index) {
              setPendingDelete(null);
              return;
            }

            // Clean up ready state for deleted text editors and videos
            if (elementToDelete.type === 'FieldTextEditor') {
              const fieldName = `text_${elementToDelete.index}`;
              setReadyTextEditors(prev => {
                const newSet = new Set(prev);
                newSet.delete(fieldName);
                return newSet;
              });
            } else if (elementToDelete.type === 'FieldTextInput') {
              const fieldName = `video_${elementToDelete.index}`;
              setReadyVideos(prev => {
                const newSet = new Set(prev);
                newSet.delete(fieldName);
                return newSet;
              });
            } else if (elementToDelete.type === 'AddImages') {
              // For AddImages elements, remove all associated images first
              const currentDynamicImageData = form.getState().values.dynamicImageData || [];

              // Find all images associated with this element
              const imageIdsToDelete = currentDynamicImageData
                .filter(d => d.elementIndex === elementToDelete.index)
                .map(d => d.imageId);

              // Also check for temporary images in dynamicImages state
              const tempImageIds = dynamicImages
                .filter(img => img.elementIndex === elementToDelete.index)
                .map(img => img.id);

              const allImageIdsToDelete = [...new Set([...imageIdsToDelete, ...tempImageIds])];

              // Mark images as being deleted (to prevent them showing as static during transition)
              setDeletingImageIds(prev => {
                const newSet = new Set(prev);
                allImageIdsToDelete.forEach(id => newSet.add(id));
                return newSet;
              });

              // Remove from dynamicImages state
              setDynamicImages(prev =>
                prev.filter(i => {
                  const imgId = i.id?.uuid || i.id;
                  return !allImageIdsToDelete.includes(imgId);
                })
              );

              // Remove from dynamicImageData
              form.change(
                'dynamicImageData',
                currentDynamicImageData.filter(d => !allImageIdsToDelete.includes(d.imageId))
              );

              // Remove the addImage_N field from form values
              form.change(`addImage_${elementToDelete.index}`, undefined);

              // Call onRemoveImage for each image
              // Once parent updates and removes from images prop, we clean up deletingImageIds in useEffect
              allImageIdsToDelete.forEach(imageId => {
                if (typeof onRemoveImage === 'function') {
                  // Normalize imageId to string format
                  const normalizedId = imageId?.uuid || imageId;
                  onRemoveImage(normalizedId);
                }
              });
            }

            setFormElements(prev => prev.filter((_, i) => i !== index));
            setPendingDelete(null);
          } else {
            // First click - mark for deletion using element's unique id
            setPendingDelete(elementId);
          }
        };

        const handleRemoveImageFromElement = (imageId, el, idx, form) => {
          // Remove from dynamicImages state
          setDynamicImages(prev => prev.filter(img => img.id !== imageId));

          // Remove from dynamicImageData
          form.change(
            'dynamicImageData',
            (form.getState().values.dynamicImageData || []).filter(
              d => d.imageId !== (imageId?.uuid || imageId)
            )
          );

          // Remove from form images array (if present)
          form.change(
            'images',
            (form.getState().values.images || []).filter(
              img => (img.id?.uuid || img.id) !== (imageId?.uuid || imageId)
            )
          );

          // Also call the prop to remove the image's correlation to the work
          if (typeof onRemoveImage === 'function') {
            // Normalize imageId to string format
            const normalizedId = imageId?.uuid || imageId;
            onRemoveImage(normalizedId);
          }

          // After removal, check if this AddImages element is now empty
          const remainingImages = dynImages.filter(img => {
            // For temp images, id is like "3__1748941426709"
            if (typeof img.id === 'string' && img.id.includes('__')) {
              const [elementIndexStr] = img.id.split('__');
              const elementIndex = parseInt(elementIndexStr, 10);
              return elementIndex === el.index && img.id !== imageId;
            }
            // For uploaded images, use dynamicImageData mapping
            const mapping = (form.getState().values.dynamicImageData || []).find(
              d => d.imageId === (img.id?.uuid || img.id)
            );
            if (mapping) {
              return (
                mapping.elementIndex === el.index &&
                (img.id?.uuid || img.id) !== (imageId?.uuid || imageId)
              );
            }
            return false;
          });

          if (remainingImages.length === 0) {
            // Remove the AddImages element if no images left
            setFormElements(prev =>
              prev.filter(e => !(e.type === 'AddImages' && e.index === el.index))
            );
            // Remove the addImage_N field from form values
            form.change(`addImage_${el.index}`, undefined);
          }
        };

        // ImageCropModal clears its crop whenever it gets a new image
        const handleImageChange = (file, target, idx) => {
          setCropErrorTarget(null);
          // Reset aspect ratio to 16/9 for static images
          if (target.type === 'static') {
            setAspect(16 / 9);
          }
          const imageReader = new FileReader();
          imageReader.addEventListener('load', () => {
            // convert image file to base64 string
            setCropImageSrc(imageReader.result.toString());
            setSelectionModalOpen(true);
          });
          setCurrentImageTarget(target);
          // Only set the field for static images
          if (target.type === 'static') {
            form.change(`addImage`, file);
            form.blur(`addImage`);
          }
          if (target.type === 'dynamic') {
            setUploadingElement(idx);
          }
          imageReader.readAsDataURL(file);
          setSelectionModalOpen(true);
          form.blur('submit');
        };

        const showAddToArray = values?.experience?.length < 10;

        const locale = config.locale;
        const translation = locale === 'fi' ? 'fiTranslation' : 'enTranslation';

        const getAvailableSkillOptions = index => {
          const selectedSkillIds = values.experience
            .map((item, idx) => idx !== index && item.category && item.category.key)
            .filter(Boolean);

          const availableSkillOptions = skillOptions
            .filter(option => !selectedSkillIds.includes(option.key))
            .sort((a, b) =>
              a[translation].localeCompare(b[translation], locale, { sensitivity: 'base' })
            );

          return availableSkillOptions;
        };

        // Function to filter out selected skills
        const getAvailableToolOptions = index => {
          const selectedToolIds = values.tools
            .map((item, idx) => idx !== index && item.category && item.category.key)
            .filter(Boolean);

          const availableToolOptions = toolOptions
            .filter(option => !selectedToolIds.includes(option.key))
            .sort((a, b) =>
              a[translation].localeCompare(b[translation], locale, { sensitivity: 'base' })
            );

          return availableToolOptions.map(option => ({
            ...option,
          }));
        };

        const submitDisabled =
          inProgress ||
          imageLoading ||
          submitting ||
          savingDraft ||
          hasValidationErrors ||
          invalid ||
          staticImages.length < 1;

        // Drafts may be incomplete: only a title is needed to save one
        const saveDraftDisabled =
          inProgress ||
          imageLoading ||
          submitting ||
          savingDraft ||
          !(values.title && values.title.trim().length > 0);

        const isStepOneActive = currentStep === 1;
        const isStepTwoActive = currentStep === 2;
        const [createStepConfig, detailsStepConfig] = stepConfig;

        const openDetailsSidebar = e => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          setDetailsSidebarOpen(true);
          // Scroll sidebar to top when it opens
          setTimeout(() => {
            if (detailsSidebarRef.current) {
              detailsSidebarRef.current.scrollTop = 0;
            }
          }, 50);
        };

        const closeDetailsSidebar = () => {
          setDetailsSidebarOpen(false);
        };

        const rootClasses = classNames(classes, detailsSidebarOpen && css.withSidebar);

        return (
          <Form className={rootClasses} onSubmit={handleSubmit}>
            {/* Report the dirty state to the page for its unsaved-changes guard */}
            <FormSpy
              subscription={{ dirty: true }}
              onChange={({ dirty }) => {
                if (typeof onDirtyChange === 'function') {
                  onDirtyChange(dirty);
                }
              }}
            />
            {/* Overlay for mobile */}
            <div
              className={classNames(css.sidebarOverlay, detailsSidebarOpen && css.visible)}
              onClick={closeDetailsSidebar}
            />

            {/* Main Content */}
            <div className={classNames(css.mainContent, detailsSidebarOpen && css.withSidebar)}>
              <div className={css.creationStage}>
                <TitleInputWithPlaceholder
                  id="title"
                  name="title"
                  label={titleMessage}
                  placeholder={titlePlaceholderMessage}
                  maxLength={TITLE_MAX_LENGTH}
                  validate={composeValidators(
                    required(titleRequiredMessage),
                    maxLength60Message,
                    noLinks(titleNoLinksMessage)
                  )}
                />

                <AddPortfolioImages
                  className={css.staticImageField}
                  images={staticImages}
                  thumbnailClassName={classNames(
                    css.thumbnail,
                    values.imageRadius === 'true' ? css.roundedThumbnail : null
                  )}
                  savedImageAltText={intl.formatMessage({
                    id: 'EditListingPhotosForm.savedImageAltText',
                  })}
                  onRemoveImage={onRemoveImage}
                  imageUploadInProgress={imageUploadInProgress}
                  showDeleteButton={true}
                  showCoverBadge={true}
                  renderAdditionalContent={(image, index) => {
                    if (index === 0 && staticImages.length > 0) {
                      return (
                        <div className={css.imageRadiusSelectorOverlay}>
                          <ImageRadiusSelector
                            name="imageRadius"
                            validate={required(roundedCornersRequired)}
                          />
                        </div>
                      );
                    }
                    return null;
                  }}
                >
                  {/* The field stays registered so a picked cover image marks the form dirty */}
                  <Field
                    id="addImage"
                    name="addImage"
                    accept={ACCEPT_IMAGES}
                    form={null}
                    label={addFirstImageMessage}
                    type="file"
                    disabled={imageUploadRequested || (staticImages && staticImages.length >= 1)}
                  >
                    {fieldprops => {
                      const { accept, input, label, disabled: fieldDisabled } = fieldprops;
                      if (fieldDisabled) return null;
                      return (
                        <ImageDropzone
                          id={input.name}
                          accept={accept}
                          hint={label}
                          onFileSelected={file =>
                            handleImageChange(
                              file,
                              {
                                type: 'static',
                                index: null,
                              },
                              null
                            )
                          }
                        />
                      );
                    }}
                  </Field>

                  <Field
                    component={props => {
                      const { input, meta } = props;
                      return (
                        <div className={css.imageRequiredWrapper}>
                          <input {...input} />
                          <ValidationError fieldMeta={meta} />
                        </div>
                      );
                    }}
                    name="images"
                    type="hidden"
                  />
                </AddPortfolioImages>
                {cropErrorFor({ type: 'static', index: null })}
              </div>

              {formElements.map((el, idx) => {
                // Check if user can add elements (has title and cover image)
                const hasTitle = values.title && values.title.trim().length > 0;
                const hasCoverImage = staticImages && staticImages.length > 0;
                const canAddElements = hasTitle && hasCoverImage;

                const addElementButtons = (
                  <div className={css.insertElementButtons}>
                    <SecondaryButton
                      type="button"
                      className={css.insertButton}
                      onClick={() => addFormElement('AddImages', idx)}
                      disabled={!canAddElements}
                    >
                      <FaRegImage className={css.addElementIcon} />
                      <span className={css.insertButtonText}>
                        <FormattedMessage id="EditPortfolioWorkForm.addImagesButton" />
                      </span>
                    </SecondaryButton>
                    <SecondaryButton
                      type="button"
                      className={css.insertButton}
                      onClick={() => addFormElement('FieldTextEditor', idx)}
                      disabled={!canAddElements}
                    >
                      <FiType className={css.addElementIcon} />
                      <span className={css.insertButtonText}>
                        <FormattedMessage id="EditPortfolioWorkForm.addTextButton" />
                      </span>
                    </SecondaryButton>
                    <SecondaryButton
                      type="button"
                      className={css.insertButton}
                      onClick={() => addFormElement('FieldTextInput', idx)}
                      disabled={!canAddElements}
                    >
                      <FaVideo className={css.addElementIcon} />
                      <span className={css.insertButtonText}>
                        <FormattedMessage id="EditPortfolioWorkForm.addVideoButton" />
                      </span>
                    </SecondaryButton>
                  </div>
                );

                const elementContent = (() => {
                  if (el.type === 'AddImages') {
                    const elementImages = dynImages.filter(img => {
                      if (typeof img.id === 'string' && img.id.includes('__')) {
                        const [elementIndexStr] = img.id.split('__');
                        const elementIndex = parseInt(elementIndexStr, 10);
                        return elementIndex === el.index;
                      }
                      const mapping = dynamicImageData.find(
                        d => d.imageId === (img.id?.uuid || img.id)
                      );
                      if (mapping) {
                        return mapping.elementIndex === el.index;
                      }
                      const firstAddImagesIndex = formElements.findIndex(
                        e => e.type === 'AddImages'
                      );
                      return idx === firstAddImagesIndex;
                    });

                    const isEmpty = elementImages.length === 0;

                    return (
                      <div className={css.imagesFieldWrapper} key={el.id}>
                        <div className={css.imagesButtonsWrapper}>
                          <div className={css.leftButtons}>
                            <div className={css.moveButtonsContainer}>
                              {idx > 0 && (
                                <button
                                  type="button"
                                  className={css.moveButton}
                                  onClick={() => moveElementUp(idx)}
                                  title="Move up"
                                >
                                  <BiUpArrow />
                                </button>
                              )}
                              {idx < formElements.length - 1 && (
                                <button
                                  type="button"
                                  className={css.moveButton}
                                  onClick={() => moveElementDown(idx)}
                                  title="Move down"
                                >
                                  <BiDownArrow />
                                </button>
                              )}
                            </div>
                            <button
                              type="button"
                              className={classNames(
                                css.deleteButton,
                                pendingDelete === el.id && css.deleteButtonConfirm
                              )}
                              onClick={() => handleDelete(idx)}
                              title={
                                pendingDelete === el.id
                                  ? 'Click again to confirm deletion'
                                  : 'Delete'
                              }
                            >
                              <AiOutlineClose />
                            </button>
                          </div>
                        </div>
                        <AddPortfolioImages
                          key={el.id}
                          className={css.imagesField}
                          images={elementImages.map(img => {
                            let aspect = img.aspect;
                            if (aspect === undefined) {
                              const found = dynamicImages.find(
                                d => d.id === img.id || d.id?.uuid === img.id
                              );
                              if (found) aspect = found.aspect;
                              if (aspect === undefined && dynamicImageData) {
                                const mapping = dynamicImageData.find(
                                  d => d.imageId === (img.id?.uuid || img.id)
                                );
                                if (mapping) aspect = mapping.aspect;
                              }
                            }
                            return { ...img, aspect };
                          })}
                          thumbnailClassName={classNames(
                            css.thumbnail,
                            values.imageRadius === 'true' ? css.roundedThumbnail : null
                          )}
                          savedImageAltText={intl.formatMessage({
                            id: 'EditListingPhotosForm.savedImageAltText',
                          })}
                          onRemoveImage={(imageId, e) => {
                            if (e) e.preventDefault();
                            handleRemoveImageFromElement(imageId, el, idx, form);
                          }}
                          imageUploadInProgress={imageUploadInProgress}
                          showDeleteButton={false}
                        >
                          {isEmpty && !imageLoading && uploadingElement !== idx && (
                            <ImageDropzone
                              id={`addImage_dropzone_${el.index}`}
                              accept={ACCEPT_IMAGES}
                              onFileSelected={file =>
                                handleImageChange(file, { type: 'dynamic', index: el.index }, null)
                              }
                            />
                          )}

                          <Field
                            component={props => {
                              const { input, meta } = props;
                              return (
                                <div className={css.imageRequiredWrapper}>
                                  <input {...input} />
                                  <ValidationError fieldMeta={meta} />
                                </div>
                              );
                            }}
                            name="images"
                            type="hidden"
                          />
                        </AddPortfolioImages>
                        {cropErrorFor({ type: 'dynamic', index: el.index })}
                      </div>
                    );
                  }
                  if (el.type === 'FieldTextEditor') {
                    const fieldName = `text_${el.index}`;
                    const isReady = readyTextEditors.has(fieldName);
                    const textContent = values && values[fieldName];
                    const hasContent = hasActualContent(textContent);

                    // Handle blur - switch to ready mode if content exists.
                    // ReactQuill emits onBlur during paste/context-menu when the
                    // selection briefly moves to a hidden clipboard element.
                    // Defer one tick and skip the switch if focus has returned.
                    const handleEditorBlur = () => {
                      setTimeout(() => {
                        const editorRef = textEditorRefs.current[fieldName];
                        const quillEditor =
                          editorRef && editorRef.quillRef && editorRef.quillRef.getEditor
                            ? editorRef.quillRef.getEditor()
                            : null;
                        if (quillEditor && quillEditor.hasFocus()) return;

                        if (hasActualContent(textContent)) {
                          setReadyTextEditors(prev => {
                            const newSet = new Set(prev);
                            newSet.add(fieldName);
                            return newSet;
                          });
                        }
                      }, 0);
                    };

                    // Handle focus - switch to edit mode and set all others to ready
                    const handleEditorFocus = () => {
                      setReadyTextEditors(prev => {
                        // Get all text editor field names
                        const allTextEditorFields = formElements
                          .filter(e => e.type === 'FieldTextEditor')
                          .map(e => `text_${e.index}`);

                        // Create new set with all editors except the current one
                        const newSet = new Set();
                        allTextEditorFields.forEach(field => {
                          if (field !== fieldName) {
                            const fieldContent = values && values[field];
                            // Only add to ready set if it has content
                            if (hasActualContent(fieldContent)) {
                              newSet.add(field);
                            }
                          }
                        });

                        return newSet;
                      });
                    };

                    // Handle preview click - switch to edit mode with focus
                    const handlePreviewClick = () => {
                      // First update the state
                      setReadyTextEditors(prev => {
                        // Get all text editor field names
                        const allTextEditorFields = formElements
                          .filter(e => e.type === 'FieldTextEditor')
                          .map(e => `text_${e.index}`);

                        // Create new set with all editors except the current one
                        const newSet = new Set();
                        allTextEditorFields.forEach(field => {
                          if (field !== fieldName) {
                            const fieldContent = values && values[field];
                            // Only add to ready set if it has content
                            if (hasActualContent(fieldContent)) {
                              newSet.add(field);
                            }
                          }
                        });

                        return newSet;
                      });

                      // Then focus the editor
                      setTimeout(() => {
                        const editorRef = textEditorRefs.current[fieldName];
                        if (editorRef && editorRef.quillRef) {
                          const editor = editorRef.quillRef.getEditor();
                          if (editor) {
                            editor.focus();
                          }
                        }
                      }, 50);
                    };

                    return (
                      <div className={css.textEditorWrapper} key={el.id}>
                        {/* Always render the editor to keep form field connected */}
                        <Field name={fieldName}>
                          {({ meta }) => (
                            <>
                              <div
                                style={{
                                  display: isReady && hasContent ? 'none' : 'block',
                                  width: '100%',
                                }}
                              >
                                <FieldTextEditor
                                  ref={ref => {
                                    textEditorRefs.current[fieldName] = ref;
                                  }}
                                  key={el.id}
                                  id={fieldName}
                                  name={fieldName}
                                  className={css.fieldTextEditor}
                                  type="textarea"
                                  placeholder={'Description'}
                                  quillClass={css.quillClass}
                                  validate={composeValidators(
                                    textEditorValidation(externalLinksMessage)
                                  )}
                                  isPortfolioWork
                                  onFocus={handleEditorFocus}
                                  onBlur={handleEditorBlur}
                                />
                              </div>
                              {/* Preview mode - shown when ready */}
                              {isReady && hasContent && (
                                <>
                                  <div
                                    className={css.textEditorPreviewContainer}
                                    onClick={handlePreviewClick}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div
                                      className={css.textEditorPreviewContent}
                                      dangerouslySetInnerHTML={{ __html: textContent || '' }}
                                    />
                                  </div>
                                  {/* Show validation error in preview mode */}
                                  {meta.error && (
                                    <div className={css.textEditorPreviewError}>{meta.error}</div>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Field>
                        <div className={css.textEditorButtonsWrapper}>
                          <div className={css.leftButtons}>
                            <div className={css.moveButtonsContainer}>
                              {idx > 0 && (
                                <button
                                  type="button"
                                  className={css.moveButton}
                                  onClick={() => moveElementUp(idx)}
                                  title="Move up"
                                >
                                  <BiUpArrow />
                                </button>
                              )}
                              {idx < formElements.length - 1 && (
                                <button
                                  type="button"
                                  className={css.moveButton}
                                  onClick={() => moveElementDown(idx)}
                                  title="Move down"
                                >
                                  <BiDownArrow />
                                </button>
                              )}
                            </div>
                            {isReady && hasContent ? (
                              <button
                                type="button"
                                className={css.editButton}
                                onClick={handlePreviewClick}
                                title="Edit text"
                              >
                                <FiEdit2 />
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={css.checkButton}
                                onClick={() => toggleTextEditorReady(fieldName, false)}
                                title="Mark as ready"
                                disabled={!hasContent}
                              >
                                <AiOutlineCheck />
                              </button>
                            )}
                            <button
                              type="button"
                              className={classNames(
                                css.deleteButton,
                                pendingDelete === el.id && css.deleteButtonConfirm
                              )}
                              onClick={() => handleDelete(idx)}
                              title={
                                pendingDelete === el.id
                                  ? 'Click again to confirm deletion'
                                  : 'Delete'
                              }
                            >
                              <AiOutlineClose />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  if (el.type === 'FieldTextInput') {
                    const fieldName = `video_${el.index}`;
                    const value = values && values[fieldName];
                    const isReady = readyVideos.has(fieldName);

                    const videoAnalysis = analyzeVideoUrl(value);
                    const embedIframe = videoAnalysis.embedElement;
                    const showInstructions = !value || !embedIframe;
                    const showError =
                      value && videoAnalysis.isValidFormat && !videoAnalysis.hasValidId;

                    return (
                      <div className={css.videoContainer} key={el.id}>
                        <div className={css.videoInputWrapper}>
                          {/* Always render the input to keep form field connected */}
                          <div
                            style={{
                              display: isReady && embedIframe ? 'none' : 'block',
                              width: '100%',
                            }}
                          >
                            <FieldTextInput
                              key={el.id}
                              id={fieldName}
                              name={fieldName}
                              className={css.videoInput}
                              type="text"
                              placeholder={videoInputLabel}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                }
                              }}
                            />
                            {/* Show instructions when no URL is entered OR when embed fails */}
                            {showInstructions && (
                              <div className={css.videoInstructions}>
                                <div className={css.videoInstructionsTitle}>
                                  <FormattedMessage id="EditPortfolioWorkForm.videoInstructions" />
                                </div>
                                {showError && (
                                  <div className={css.videoInstructionsError}>
                                    <FormattedMessage id="EditPortfolioWorkForm.videoUrlFormatError" />
                                  </div>
                                )}
                                <ul className={css.videoInstructionsList}>
                                  <li
                                    className={classNames(
                                      css.videoInstructionItem,
                                      videoAnalysis.detectedService === 'youtube' && css.highlighted
                                    )}
                                  >
                                    <span className={css.videoInstructionPlatform}>YouTube</span>
                                    <code className={css.videoInstructionExample}>
                                      <FormattedMessage id="EditPortfolioWorkForm.videoInstructionsYouTube" />
                                    </code>
                                  </li>
                                  <li
                                    className={classNames(
                                      css.videoInstructionItem,
                                      videoAnalysis.detectedService === 'vimeo' && css.highlighted
                                    )}
                                  >
                                    <span className={css.videoInstructionPlatform}>Vimeo</span>
                                    <code className={css.videoInstructionExample}>
                                      <FormattedMessage id="EditPortfolioWorkForm.videoInstructionsVimeo" />
                                    </code>
                                  </li>
                                  <li
                                    className={classNames(
                                      css.videoInstructionItem,
                                      videoAnalysis.detectedService === 'tiktok' && css.highlighted
                                    )}
                                  >
                                    <span className={css.videoInstructionPlatform}>TikTok</span>
                                    <code className={css.videoInstructionExample}>
                                      <FormattedMessage id="EditPortfolioWorkForm.videoInstructionsTikTok" />
                                    </code>
                                  </li>
                                  <li
                                    className={classNames(
                                      css.videoInstructionItem,
                                      videoAnalysis.detectedService === 'instagram' &&
                                        css.highlighted
                                    )}
                                  >
                                    <span className={css.videoInstructionPlatform}>Instagram</span>
                                    <code className={css.videoInstructionExample}>
                                      <FormattedMessage id="EditPortfolioWorkForm.videoInstructionsInstagram" />
                                    </code>
                                  </li>
                                </ul>
                              </div>
                            )}
                          </div>
                          {/* Buttons always visible */}
                          <div className={css.textEditorButtonsWrapper}>
                            <div className={css.leftButtons}>
                              <div className={css.moveButtonsContainer}>
                                {idx > 0 && (
                                  <button
                                    type="button"
                                    className={css.moveButton}
                                    onClick={() => moveElementUp(idx)}
                                    title="Move up"
                                  >
                                    <BiUpArrow />
                                  </button>
                                )}
                                {idx < formElements.length - 1 && (
                                  <button
                                    type="button"
                                    className={css.moveButton}
                                    onClick={() => moveElementDown(idx)}
                                    title="Move down"
                                  >
                                    <BiDownArrow />
                                  </button>
                                )}
                              </div>
                              <button
                                type="button"
                                className={classNames(
                                  css.deleteButton,
                                  pendingDelete === el.id && css.deleteButtonConfirm
                                )}
                                onClick={() => handleDelete(idx)}
                                title={
                                  pendingDelete === el.id
                                    ? 'Click again to confirm deletion'
                                    : 'Delete'
                                }
                              >
                                <AiOutlineClose />
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Video preview - shown always when embedIframe exists */}
                        {embedIframe ? (
                          <div
                            className={classNames(
                              css.videoPreview,
                              value && value.includes('instagram.com/reel/')
                                ? css.instagramContainer
                                : '',
                              values.imageRadius === 'true' ? css.imageWithRadius : ''
                            )}
                          >
                            {embedIframe}
                          </div>
                        ) : null}
                      </div>
                    );
                  }
                  return null;
                })();

                return (
                  <div key={el.id} className={css.formElement}>
                    <div className={css.elementContent}>{elementContent}</div>
                  </div>
                );
              })}

              {/* Check if user can add elements at the bottom */}
              {(() => {
                const hasTitle = values.title && values.title.trim().length > 0;
                const hasCoverImage = staticImages && staticImages.length > 0;
                const canAddElements = hasTitle && hasCoverImage;

                return (
                  <>
                    <div className={css.addFormElementButtons}>
                      <SecondaryButton
                        type="button"
                        onClick={() => addFormElement('AddImages')}
                        className={css.insertButton}
                        disabled={!canAddElements}
                      >
                        <FaRegImage className={css.addElementIcon} />
                        <span className={css.insertButtonText}>
                          <FormattedMessage id="EditPortfolioWorkForm.addImagesButton" />
                        </span>
                        <FaPlus className={css.insertButtonMobileText} />
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => addFormElement('FieldTextEditor')}
                        className={css.insertButton}
                        disabled={!canAddElements}
                      >
                        <FiType className={css.addElementIcon} />
                        <span className={css.insertButtonText}>
                          <FormattedMessage id="EditPortfolioWorkForm.addTextButton" />
                        </span>
                        <FaPlus className={css.insertButtonMobileText} />
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={() => addFormElement('FieldTextInput')}
                        className={css.insertButton}
                        disabled={!canAddElements}
                      >
                        <FaVideo className={css.addElementIcon} />
                        <span className={css.insertButtonText}>
                          <FormattedMessage id="EditPortfolioWorkForm.addVideoButton" />
                        </span>
                        <FaPlus className={css.insertButtonMobileText} />
                      </SecondaryButton>
                    </div>
                  </>
                );
              })()}

              {/* Button to open sidebar at bottom of mainContent */}
              <div className={css.inlineButtonsContainer}>
                {showPublishButton && (
                  <SecondaryButton
                    type="button"
                    className={css.saveDraftButtonInline}
                    onClick={() => handleSaveDraft(form)}
                    inProgress={savingDraft}
                    disabled={saveDraftDisabled}
                  >
                    <FormattedMessage id="EditPortfolioWorkForm.saveDraftButton" />
                  </SecondaryButton>
                )}
                <SecondaryButton
                  type="button"
                  onClick={openDetailsSidebar}
                  className={css.openSidebarButtonInline}
                >
                  <FormattedMessage
                    id={
                      isNewPortfolioWorkFlow
                        ? 'EditPortfolioWorkForm.projectDetailsAdd'
                        : 'EditPortfolioWorkForm.projectDetails'
                    }
                    defaultMessage="Open Project Details"
                  />
                  <GoSidebarExpand className={css.openSidebarButtonInlineIcon} />
                </SecondaryButton>
              </div>
            </div>
            {/* End Main Content */}

            {/* Floating button to open details sidebar */}
            <button
              type="button"
              className={classNames(css.openSidebarButton, detailsSidebarOpen && css.hidden)}
              onClick={openDetailsSidebar}
              aria-label="Open project details"
            >
              <GoSidebarExpand className={css.openSidebarButtonIcon} />
              <span className={css.openSidebarButtonText}>
                <FormattedMessage
                  id="EditPortfolioWorkForm.projectDetails"
                  defaultMessage="Project Details"
                />
              </span>
            </button>

            {/* Details Sidebar */}
            <div
              ref={detailsSidebarRef}
              className={classNames(css.detailsSidebar, detailsSidebarOpen && css.open)}
            >
              <div className={css.detailsSidebarHeader}>
                <button
                  type="button"
                  className={css.detailsSidebarClose}
                  onClick={closeDetailsSidebar}
                  aria-label="Close details"
                >
                  <GoSidebarCollapse />
                </button>
                <h2 className={css.detailsSidebarTitle}>
                  {detailsStepConfig?.title || 'Project Details'}
                </h2>
              </div>

              <div className={css.detailsSidebarContent}>
                <div className={css.relatedWorkSelectionWrapper}>
                  <label htmlFor={'relatedWorkExperienceCustomer'} className={css.relatedWorkLabel}>
                    {relatedWorkExperienceLabel}
                  </label>

                  <div className={css.relatedSkillsFieldArray}>
                    <FieldTextInput
                      id="relatedWorkExperienceCustomer"
                      name="relatedWorkExperienceCustomer"
                      className={classNames(css.relatedWorkArrayOption, css.skillsCategory)}
                      type="text"
                      placeholder={intl.formatMessage({
                        id: 'EditPortfolioWorkForm.relatedWorkExperiencePlaceholder',
                      })}
                      validate={composeValidators(
                        required(relatedWorkExperienceRequired),
                        maxLength60Message
                      )}
                    />
                  </div>
                </div>
                <div className={css.relatedWorkExperienceWrapper}>
                  <div className={css.relatedWorkCustomerTypeWrapper}>
                    <label htmlFor={'relatedWorkCustomerType'} className={css.relatedWorkLabel}>
                      {relatedWorkCustomerTypeLabel}
                    </label>
                    <div className={css.relatedWorkCustomerTypeRadioWrapper}>
                      <FieldRadioButton
                        id={`relatedWorkCustomerType`}
                        name={`relatedWorkCustomerType`}
                        label={relatedWorkCustomerTypeDirectLabel}
                        value="directCustomer"
                        checkboxIcon
                        validate={required(relatedWorkCustomerTypeRequired)}
                      />
                      <FieldRadioButton
                        id={`relatedWorkCustomerType1`}
                        name={`relatedWorkCustomerType`}
                        label={relatedWorkCustomerTypeAgencyLabel}
                        value="agency"
                        checkboxIcon
                        validate={required(relatedWorkCustomerTypeRequired)}
                      />
                    </div>
                    {values.relatedWorkCustomerType &&
                      values.relatedWorkCustomerType === 'agency' && (
                        <div className={css.relatedWorkCustomerTypeAgencyDescription}>
                          {relatedWorkCustomerTypeAgencyDescription}
                        </div>
                      )}
                  </div>

                  {values.relatedWorkCustomerType && values.relatedWorkCustomerType === 'agency' ? (
                    <div className={css.relatedWorkAgencyWrapper}>
                      <label
                        htmlFor={'relatedWorkExperienceAgency'}
                        className={css.relatedWorkLabel}
                      >
                        {relatedWorkExperienceCustomerNameLabel}
                      </label>
                      <FieldTextInput
                        id="relatedWorkExperienceAgency"
                        name="relatedWorkExperienceAgency"
                        className={css.relatedWorkAgency}
                        type="text"
                        placeholder={intl.formatMessage({
                          id: 'EditPortfolioWorkForm.relatedWorkExperienceAgencyPlaceholder',
                        })}
                        maxLength={TITLE_MAX_LENGTH}
                        validate={composeValidators(
                          required(
                            intl.formatMessage({
                              id: 'EditPortfolioWorkForm.relatedWorkExperienceAgencyRequired',
                            })
                          )
                        )}
                        autoFocus={false}
                      />
                    </div>
                  ) : null}
                </div>
                <div className={css.datesContainer}>
                  <div className={css.datesStartContainer}>
                    <FieldSelectModern
                      className={css.dateSelectMonth}
                      id={`startMonth`}
                      name={`startMonth`}
                      options={generateMonthsList()}
                      placeholder={monthPlaceholder}
                      label={startLabel}
                      disabled={values.dontAddWorkedWith}
                      validate={required(startMonthRequired)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (!invalid) {
                            handleSubmit(form.getState().values, form);
                          }
                        }
                      }}
                    />
                    <FieldSelectModern
                      className={css.dateSelectYear}
                      id={`startYear`}
                      name={`startYear`}
                      options={generateYearsList(1950)}
                      placeholder={yearStartPlaceholder}
                      disabled={values.dontAddWorkedWith}
                      validate={required(startYearRequired)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (!invalid) {
                            handleSubmit(form.getState().values, form);
                          }
                        }
                      }}
                    />
                  </div>
                  {!hideEndDate && (
                    <div className={css.datesEndContainer}>
                      <FieldSelectModern
                        className={css.dateSelectMonth}
                        id={`endMonth`}
                        name={`endMonth`}
                        options={generateMonthsList()}
                        placeholder={monthPlaceholder}
                        label={endLabel}
                        disabled={values.dontAddWorkedWith}
                        validate={!values.jobContinues ? required(endMonthRequired) : undefined}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!invalid) {
                              handleSubmit(form.getState().values, form);
                            }
                          }
                        }}
                      />
                      <FieldSelectModern
                        className={css.dateSelectYear}
                        id={`endYear`}
                        name={`endYear`}
                        options={generateYearsList(values.startYear.value || 1950)}
                        placeholder={yearPlaceholder}
                        disabled={values.dontAddWorkedWith}
                        validate={!values.jobContinues ? required(endYearRequired) : undefined}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!invalid) {
                              handleSubmit(form.getState().values, form);
                            }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <FieldCheckbox
                  id={`jobContinues`}
                  name={`jobContinues`}
                  label={workContinuesLabel}
                  disabled={values.dontAddWorkedWith}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!invalid) {
                        handleSubmit(form.getState().values, form);
                      }
                    }
                  }}
                />
                <div className={css.skillsAndToolsWrapper}>
                  <div className={css.skillsWrapper}>
                    <label htmlFor={'experience'} className={css.skillsLabel}>
                      {skillsLabel}
                    </label>
                    <FieldArray name="experience">
                      {({ fields }) => (
                        <>
                          {fields.map((name, index) => (
                            <div key={name} className={css.skillsFieldArray}>
                              <FieldSelectModern
                                id={`${name}.category`}
                                name={`${name}.category`}
                                options={getAvailableSkillOptions(index)}
                                components={{ Placeholder }}
                                className={classNames(css.fieldArrayOption, css.skillsCategory)}
                                validate={required(categoryRequired)}
                              />
                              <span
                                className={css.skillsDelete}
                                onClick={() =>
                                  fields?.length === 1
                                    ? fields.update(index, {})
                                    : fields.remove(index)
                                }
                              >
                                <IconClose rootClassName={css.deleteIcon} />
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                    </FieldArray>
                    {showAddToArray && (
                      <InlineTextButton
                        type="button"
                        className={css.addToArray}
                        onClick={() => push('experience', {})}
                      >
                        {addButtonText}
                      </InlineTextButton>
                    )}
                  </div>
                  <div className={css.toolsWrapper}>
                    <label htmlFor={'tools'} className={css.skillsLabel}>
                      {toolsLabel}
                    </label>
                    <FieldArray name="tools">
                      {({ fields }) => (
                        <>
                          {fields.map((name, index) => (
                            <div key={name} className={css.skillsFieldArray}>
                              <FieldSelectModern
                                id={`${name}.category`}
                                name={`${name}.category`}
                                options={getAvailableToolOptions(index)}
                                components={{ Placeholder }}
                                className={classNames(css.fieldArrayOption, css.skillsCategory)}
                                validate={required(categoryRequired)}
                              />
                              <span
                                className={css.skillsDelete}
                                onClick={() =>
                                  fields?.length === 1
                                    ? fields.update(index, {})
                                    : fields.remove(index)
                                }
                              >
                                <IconClose rootClassName={css.deleteIcon} />
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                    </FieldArray>
                    {showAddToArray && (
                      <InlineTextButton
                        type="button"
                        className={css.addToArray}
                        onClick={() => push('tools', {})}
                      >
                        {addButtonText}
                      </InlineTextButton>
                    )}
                  </div>
                </div>
                <div className={css.relatedWorkCustomerTypeWrapper}>
                  <div className={css.privateWorkLabel}>
                    {privateWorkLabel}
                    <Tooltip
                      messageId="EditPortfolioWorkForm.privateWorkTooltip"
                      className={css.toolTipIcon}
                    >
                      <AiOutlineInfoCircle />
                    </Tooltip>
                  </div>
                  <div className={css.privateWorkRadioWrapper}>
                    <FieldRadioButton
                      id="privateWork"
                      name="privateWork"
                      label={intl.formatMessage({
                        id: 'EditPortfolioWorkForm.privateWorkLabelPublic',
                      })}
                      value="false"
                      checkboxIcon
                      validate={required(privateWorkRequired)}
                    />
                    <FieldRadioButton
                      id="privateWork2"
                      name="privateWork"
                      label={intl.formatMessage({
                        id: 'EditPortfolioWorkForm.privateWorkLabelPrivate',
                      })}
                      value="true"
                      checkboxIcon
                      validate={required(privateWorkRequired)}
                    />
                  </div>
                </div>
              </div>

              {/* Submit buttons in sidebar */}
              <div className={css.submitButtonWrapper}>
                <SecondaryButton
                  type="button"
                  className={css.cancelButton}
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  <FormattedMessage id="EditPortfolioWorkForm.cancelButton" />
                </SecondaryButton>
                {showPublishButton && (
                  <SecondaryButton
                    type="button"
                    className={css.saveDraftButton}
                    onClick={() => handleSaveDraft(form)}
                    inProgress={savingDraft}
                    disabled={saveDraftDisabled}
                  >
                    <FormattedMessage id="EditPortfolioWorkForm.saveDraftButton" />
                  </SecondaryButton>
                )}
                {/* {showPublishButton && (
                  <SecondaryButton
                    type="submit"
                    inProgress={submitting}
                    disabled={submitDisabled}
                    className={css.publishButton}
                    onClick={() => setSubmitAction('publish')}
                  >
                    <FormattedMessage id="EditPortfolioWorkForm.publishButton" />
                  </SecondaryButton>
                )} */}
                <PrimaryButton
                  type="submit"
                  inProgress={submitting}
                  disabled={submitDisabled}
                  className={css.submitButton}
                  onClick={() => setSubmitAction('submit')}
                >
                  {submitButtonText}
                </PrimaryButton>
              </div>
            </div>

            <ImageCropModal
              id="EditListingPhotosForm.selectImageArea"
              isOpen={selectionModalOpen}
              src={cropImageSrc}
              // Temp image id: element index + timestamp. Dynamic image lookups split on '__'.
              fileName={`${currentImageTarget.index}__${Date.now()}`}
              aspect={aspect}
              aspectChangeable={currentImageTarget.type === 'dynamic'}
              onAspectChange={setAspect}
              instructions={
                currentImageTarget.type === 'static' ? (
                  <FormattedMessage id="EditListingPhotosForm.selectImageAreaInstructions" />
                ) : null
              }
              confirmLabel={<FormattedMessage id="EditListingPhotosForm.saveChanges" />}
              onConfirm={handleSubmitSelection}
              onError={handleCropError}
              onClose={() => setSelectionModalOpen(false)}
              onManageDisableScrolling={onManageDisableScrolling}
            />
            {/* Hidden file input for triggering uploads */}
            <input
              type="file"
              accept={ACCEPT_IMAGES}
              ref={hiddenFileInputRef}
              style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files[0];
                if (file) {
                  handleImageChange(
                    file,
                    { type: 'dynamic', index: currentImageTarget.index },
                    null
                  );
                }
                // Reset the input value so the same file can be selected again
                e.target.value = '';
              }}
            />
          </Form>
        );
      }}
    />
  );
};

PortfolioWorkFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
};

PortfolioWorkFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,
  inProgress: bool,
};

const PortfolioWorkForm = compose(injectIntl)(PortfolioWorkFormComponent);

PortfolioWorkForm.displayName = 'PortfolioWorkForm';

export default PortfolioWorkForm;
